/**
 * Created by simon zhao on 5/5/2022.
 * Description:
 * DiagramAutomationRender Component
 * ------ maintenance history ------
 * Updated by Daniel Wang on 5/31/2023, added creating a sidenote.
 * Updated by Daniel Wang on 7/3/2023, added updating a note.
 */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { AppState } from '../../../redux';
import { AutomationModelBase } from '../../../tamalelibs/models/automation-base.model';
import { AutomationNoteMappingModel, AutomationRelationshipModel } from '../../../tamalelibs/models/automation.model';
import { EntryType } from '../../../tamalelibs/models/entry-type.model';
import { WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { AlertWindowService } from '../../alert-window/alert-window.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../tam-context-menu/tam-context-menu.model';
import { DiagramNoteAutomationDialogOptions } from '../diagram-automation-dialog/diagram-note-automation-dialog/diagram-note-automation-dialog.model';
import { DiagramAutomationNoteDialogService } from '../diagram-automation-dialog/diagram-note-automation-dialog/diagram-note-automation-dialog.service';
import { DiagramRelationshipDialogOptions } from '../diagram-automation-dialog/diagram-relationship-automation-dialog/diagram-relationship-automation-dialog.model';
import { DiagramRelationshipDialogService } from '../diagram-automation-dialog/diagram-relationship-automation-dialog/diagram-relationship-automation-dialog.service';
import { AutomationActionType } from '../diagram-context-menu/diagram-context-menu.model';
import { DiagramService } from '../diagram.service';

@Component({
    selector: 'tam-diagram-automation-render',
    templateUrl: './diagram-automation-render.component.html',
    styleUrls: ['./diagram-automation-render.component.scss']
})
export class DiagramAutomationRenderComponent implements OnInit, OnDestroy {
    //#region public properties
    @Input() automationConfigs: Array<AutomationModelBase> = [];
    /**
     * the id of a node or branch, to which the automation is going to be added.
     */
    @Input() targetId: string;
    /**
     * the automation card's context menu config.
     */
    automationContextMenuConfig: ContextMenuConfig;
    //#endregion

    //#region private properties
    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];
    private _noteTypesCache: Array<EntryType> = [];
    //#endregion

    // #region constructor
    constructor(
        private _alertWindow: AlertWindowService,
        private _diagramAutomationDialogService: DiagramAutomationNoteDialogService,
        private _diagramRelationDialogService: DiagramRelationshipDialogService,
        private _nodesService: DiagramService,
        private _store: Store<AppState>,
        private _workflowService: WorkflowService
    ) {
    }
    // #endregion

    //#region public methods
    ngOnInit() {
        this._destroySubscriptions.push(
            this._diagramAutomationDialogService.getNoteTypes().subscribe(res => {
                this._noteTypesCache = Array.from(res.entryTypes);
            }));
        this._initContextMenu();
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    /**
     * get automation card content
     * @param autoItem automation item
     * @returns
     */
    getAutomationCardContent(autoItem: AutomationModelBase) {
        return this._workflowService.getAutomationCardContent(autoItem, this._noteTypesCache);
    }

    onAutomationCardClick(autoConfig: AutomationModelBase) {
        const showDialogFunc = (cfg: AutomationModelBase) => {
            switch (cfg.objectType) {
                case 'entry': {
                    const options = new DiagramNoteAutomationDialogOptions();
                    options.nodeId = this.targetId;
                    options.isUpdate = true;
                    options.existingMapping = <AutomationNoteMappingModel>cfg;
                    if (cfg.action === 'update') {
                        if (options.existingMapping && options.existingMapping.mapping) {
                            options.existingMapping.mapping = options.existingMapping.mapping.filter(item => item.fieldInTemplate !== 'Submitter');
                        }
                        options.type = AutomationActionType.UpdateANote;
                    } else {
                        options.type = AutomationActionType.CreateANote;
                    }
                    options.taskArray = this._nodesService.getAllTaskNodes().map(node => ({ key: node.name, value: node.id }));
                    this._diagramAutomationDialogService.dialogOpen$.next(options);
                    break;
                }
                case 'sideEntry': {
                    const options = new DiagramNoteAutomationDialogOptions();
                    options.nodeId = this.targetId;
                    options.isUpdate = true;
                    options.existingMapping = <AutomationNoteMappingModel>cfg;
                    if (options.existingMapping && options.existingMapping.mapping) {
                        options.existingMapping.mapping = options.existingMapping.mapping.filter(item => item.fieldInTemplate !== 'Display Date');
                    }
                    options.taskArray = this._nodesService.getAllTaskNodes().map(node => ({ key: node.name, value: node.id }));
                    options.type = AutomationActionType.CreateASidenote;
                    this._diagramAutomationDialogService.dialogOpen$.next(options);
                    break;
                }
                case 'relationship': {
                    const options = new DiagramRelationshipDialogOptions();
                    options.nodeId = this.targetId;
                    options.isUpdate = true;
                    options.existingRelations = <AutomationRelationshipModel>cfg;
                    options.taskArray = this._nodesService.getAllTaskNodes().map(node => ({ key: node.name, value: node.id }));
                    this._diagramRelationDialogService.dialogOpen$.next(options);
                    break;
                }
                default:
                    break;
            }
        };

        if (autoConfig && this.targetId) {
            showDialogFunc(autoConfig);
        }
    }

    onClickAutomationCardMenu(e: MouseEvent, autoConfig: AutomationModelBase) {
        e.stopPropagation();
        this.automationContextMenuConfig.show$.next(new ContextMenuShowAction(e, { item: autoConfig }));
    }
    //#endregion

    //#region private methods
    private _initContextMenu(): void {
        this.automationContextMenuConfig = new ContextMenuConfig();
        this.automationContextMenuConfig.stateful = false;
        this.automationContextMenuConfig.showItemIcon = false;
        this.automationContextMenuConfig.items = [
            { id: 0, text: StringLiteralsPipe.translate('general.edit') },
            { id: 1, text: StringLiteralsPipe.translate('general.delete') },
        ];
        this._destroySubscriptions.push(
            this.automationContextMenuConfig.events$.subscribe(event => {
                if (event.payload.id === 1) {
                    this._alertWindow.warn(StringLiteralsPipe.translate('general.delete_confirmation_required'),
                        [StringLiteralsPipe.translate('workflow.sure_want_to_delete_this_task'), StringLiteralsPipe.translate('workflow.anything_will_be_deleted')],
                        StringLiteralsPipe.translate('general.delete'), StringLiteralsPipe.translate('general.cancel')).pipe(
                            take(1),
                        ).subscribe((result: boolean) => {
                            if (result) {
                                if (event.payload.data && event.payload.data.item) {
                                    this.automationConfigs.splice(this.automationConfigs.indexOf(event.payload.data.item), 1);
                                    this._store.dispatch({
                                        type: WorkflowActionTypes.DELETE_AUTOMATION_NODE,
                                        payload: event.payload.data.item.id
                                    });
                                }
                            }
                        });
                } else if (event.payload.id === 0) {
                    if (event.payload.data && event.payload.data.item) {
                        this.onAutomationCardClick(event.payload.data.item);
                    }
                }
            })
        );
    }

    //#endregion
}
